import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useStoreSettings } from '../hooks/useStoreSettings';

/**
 * Učitava Meta (Facebook) Pixel za tenanta koji ga ima podešenog u
 * Podešavanja → Integracije. Bez popunjenog ID-ja ne ubacuje ništa u stranicu.
 *
 * Prati i promenu rute: SPA ne radi pun page load, pa bi bez ovoga Pixel
 * prijavio samo prvi PageView u celoj poseti.
 *
 * Renderuje null — čist side effect.
 */

// Meta pixel ID je numeričan (15-16 cifara). Filtriramo pre ubacivanja u
// stranicu da pogrešno zalepljen sadržaj (ceo <script> snippet) ne završi
// kao izvršni kod.
const PIXEL_ID_RE = /^\d{5,20}$/;

const SCRIPT_ID = 'fb-pixel-sdk';

export const FacebookPixel = () => {
  const { settings } = useStoreSettings();
  const location = useLocation();
  const initialized = useRef<string | null>(null);
  const lastPath = useRef<string | null>(null);

  const pixelId = ((settings as any)?.fb_pixel_id || '').trim();

  useEffect(() => {
    if (!pixelId || !PIXEL_ID_RE.test(pixelId)) return;
    if (initialized.current === pixelId) return;
    initialized.current = pixelId;

    const w = window as any;
    if (!w.fbq) {
      /* eslint-disable */
      // Zvanični Meta snippet (stub koji hvata pozive dok se sdk ne učita).
      const n: any = (w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      });
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      /* eslint-enable */

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
    }

    w.fbq('init', pixelId);
    w.fbq('track', 'PageView');
    // Snippet je upravo prijavio PageView za tekuću rutu — upiši je da je
    // efekat ispod ne bi prijavio drugi put.
    lastPath.current = location.pathname;
    // location.pathname namerno nije u dependency listi: init se radi jednom
    // po pixel ID-ju, a promene rute hvata efekat ispod.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixelId]);

  useEffect(() => {
    if (!initialized.current) return;
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    (window as any).fbq?.('track', 'PageView');
  }, [location.pathname]);

  return null;
};

export default FacebookPixel;
